import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Tooltip,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Save as SaveIcon,
  Send as SendIcon,
  CheckCircle as VerifiedIcon,
  Edit as EditIcon,
  Description as TemplateIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import ApiService from '../services/api.service';
import { isPlatformLevel, can } from '../utils/roleUtils';

const EmailConfig = ({ currentUser }) => {
  // Role check helper
  const isPlatform = isPlatformLevel(currentUser?.role);
  const canEdit = can(currentUser, 'settings', 'write');
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [typingCenters, setTypingCenters] = useState([]);
  const [selectedTypingCenter, setSelectedTypingCenter] = useState('');

  // Email config state
  const [config, setConfig] = useState({
    enabled: false,
    smtpHost: '',
    smtpPort: 587,
    smtpUsername: '',
    smtpPassword: '',
    smtpUseTls: true,
    fromName: '',
    fromEmail: '',
    replyToEmail: '',
    defaultCcEmails: []
  });
  const [verified, setVerified] = useState(false);
  const [verifiedAt, setVerifiedAt] = useState(null);

  // Templates state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  // Placeholder chips insert at the cursor of the field last focused (subject or body).
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  const lastFieldRef = useRef('bodyTemplate');
  const insertPlaceholder = (name) => {
    const field = lastFieldRef.current;
    const el = (field === 'subject' ? subjectRef : bodyRef).current;
    const token = `{${name}}`;
    setSelectedTemplate((prev) => {
      const value = prev?.[field] || '';
      const start = el?.selectionStart ?? value.length;
      const end = el?.selectionEnd ?? value.length;
      const next = value.slice(0, start) + token + value.slice(end);
      setTimeout(() => { if (el) { el.focus(); el.setSelectionRange(start + token.length, start + token.length); } }, 0);
      return { ...prev, [field]: next };
    });
  };
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Test email state
  const [testingEmail, setTestingEmail] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [ccEmailInput, setCcEmailInput] = useState('');

  // Email providers for quick configuration
  const emailProviders = [
    { value: 'GOOGLE', label: 'Gmail / Google Workspace', host: 'smtp.gmail.com', port: 587 },
    { value: 'ZOHO', label: 'Zoho Mail', host: 'smtppro.zoho.in', port: 587 },
    { value: 'OUTLOOK', label: 'Outlook / Office 365', host: 'smtp.office365.com', port: 587 },
    { value: 'GODADDY', label: 'GoDaddy (Professional Email)', host: 'smtpout.secureserver.net', port: 587 },
    { value: 'CUSTOM', label: 'Custom SMTP', host: '', port: 587 }
  ];

  const [selectedProvider, setSelectedProvider] = useState('CUSTOM');

  // Fetch typing centers
  useEffect(() => {
    const fetchTypingCenters = async () => {
      try {
        const response = await ApiService.getTypingCenters();
        if (response.data?.data) {
          setTypingCenters(response.data.data);
          if (response.data.data.length > 0) {
            if (currentUser?.typingCenterId) {
              setSelectedTypingCenter(currentUser.typingCenterId);
            } else {
              setSelectedTypingCenter(response.data.data[0].id);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching typing centers:', err);
        setError('Failed to load typing centers');
      }
    };
    fetchTypingCenters();
  }, [currentUser]);

  // Fetch config when typing center changes
  useEffect(() => {
    if (selectedTypingCenter) {
      fetchConfig();
      fetchTemplates();
    }
  }, [selectedTypingCenter]);

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getEmailConfig(selectedTypingCenter);
      if (response.data?.data) {
        const data = response.data.data;
        // API returns snake_case (no camel conversion), so read snake_case with a camelCase fallback.
        setConfig({
          enabled: data.enabled || false,
          smtpHost: data.smtp_host ?? data.smtpHost ?? '',
          smtpPort: data.smtp_port ?? data.smtpPort ?? 587,
          smtpUsername: data.smtp_username ?? data.smtpUsername ?? '',
          smtpPassword: '', // Never show password from server
          smtpUseTls: (data.smtp_use_tls ?? data.smtpUseTls) !== false,
          fromName: data.from_name ?? data.fromName ?? '',
          fromEmail: data.from_email ?? data.fromEmail ?? '',
          replyToEmail: data.reply_to_email ?? data.replyToEmail ?? '',
          defaultCcEmails: data.default_cc_emails ?? data.defaultCcEmails ?? []
        });
        setVerified((data.verified ?? false) || false);
        setVerifiedAt(data.verified_at ?? data.verifiedAt);

        // Detect provider from host
        const smtpHost = data.smtp_host ?? data.smtpHost;
        const provider = emailProviders.find(p => p.host === smtpHost);
        setSelectedProvider(provider ? provider.value : 'CUSTOM');
      }
    } catch (err) {
      console.error('Error fetching email config:', err);
      if (err.response?.status !== 404) {
        setError('Failed to load email configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await ApiService.getEmailTemplates(selectedTypingCenter);
      if (response.data?.data) {
        // API returns snake_case; normalize to the camelCase keys the rest of this page reads.
        const list = (response.data.data || []).map(t => ({
          ...t,
          templateType: t.template_type ?? t.templateType,
          bodyTemplate: t.body_template ?? t.bodyTemplate,
          isHtml: t.is_html ?? t.isHtml,
          isActive: t.is_active ?? t.isActive
        }));
        setTemplates(list);
      }
    } catch (err) {
      console.error('Error fetching email templates:', err);
    }
  };

  const handleProviderChange = (event) => {
    const value = event.target.value;
    setSelectedProvider(value);
    const provider = emailProviders.find(p => p.value === value);
    if (provider && provider.host) {
      setConfig(prev => ({
        ...prev,
        smtpHost: provider.host,
        smtpPort: provider.port
      }));
    }
  };

  const handleConfigChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const dataToSave = { ...config };
      // Only include password if it was changed
      if (!dataToSave.smtpPassword) {
        delete dataToSave.smtpPassword;
      }
      await ApiService.updateEmailConfig(dataToSave, selectedTypingCenter);
      setSuccess('Email configuration saved successfully');
      fetchConfig(); // Refresh to get updated data
    } catch (err) {
      console.error('Error saving email config:', err);
      setError(err.response?.data?.message || 'Failed to save email configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      setError('Please enter a test email address');
      return;
    }

    setTestingEmail(true);
    setError(null);
    setSuccess(null);
    try {
      await ApiService.sendTestEmail(testEmail, selectedTypingCenter);
      setSuccess(`Test email sent successfully to ${testEmail}`);
      setVerified(true);
      setVerifiedAt(new Date().toISOString());
    } catch (err) {
      console.error('Error sending test email:', err);
      setError(err.response?.data?.message || 'Failed to send test email. Please check your SMTP settings.');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleAddCcEmail = () => {
    if (ccEmailInput && !config.defaultCcEmails.includes(ccEmailInput)) {
      setConfig(prev => ({
        ...prev,
        defaultCcEmails: [...prev.defaultCcEmails, ccEmailInput]
      }));
      setCcEmailInput('');
    }
  };

  const handleRemoveCcEmail = (email) => {
    setConfig(prev => ({
      ...prev,
      defaultCcEmails: prev.defaultCcEmails.filter(e => e !== email)
    }));
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate({ ...template });
    setTemplateDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!selectedTemplate) return;

    setSavingTemplate(true);
    try {
      await ApiService.updateEmailTemplate(
        selectedTemplate.templateType,
        {
          subject: selectedTemplate.subject,
          bodyTemplate: selectedTemplate.bodyTemplate,
          isHtml: Boolean(selectedTemplate.isHtml ?? selectedTemplate.is_html)
        },
        selectedTypingCenter
      );
      setSuccess('Email template saved successfully');
      setTemplateDialogOpen(false);
      fetchTemplates();
    } catch (err) {
      console.error('Error saving template:', err);
      setError(err.response?.data?.message || 'Failed to save email template');
    } finally {
      setSavingTemplate(false);
    }
  };

  // Placeholders available per template type, shown in the editor so users know what they can insert.
  const TEMPLATE_VARIABLES = {
    INVOICE_SEND: [['companyName', 'Your company name'], ['customerName', 'Customer/org name'], ['invoiceNumber', 'Invoice number'], ['grandTotal', 'Total amount'], ['currency', 'Currency (AED)'], ['dueDate', 'Payment due date']],
    QUOTATION_SEND: [['companyName', 'Your company name'], ['customerName', 'Customer/org name'], ['quotationNumber', 'Quotation number'], ['grandTotal', 'Total amount'], ['currency', 'Currency (AED)'], ['validUntil', 'Validity date']],
    WORK_ORDER_CREATED: [['workOrderNumber', 'Work order number'], ['companyName', 'Your company name'], ['customerName', 'Customer / organization name'], ['organizationName', 'Organization name'], ['customerEmail', 'Contact email'], ['status', 'Work order status'], ['createdVia', 'Created via (portal / typing center)'], ['serviceCount', 'Number of services'], ['orderDate', 'Order date'], ['documentCount', 'Documents attached']],
    WORK_ORDER_COMPLETED: [['companyLogo', "Your company logo (image)"], ['companyName', 'Your company name'], ['customerName', 'Customer name'], ['workOrderNumber', 'Work order number'], ['serviceName', 'Service name'], ['transactionNumber', 'Transaction number(s)'], ['completionDate', 'Completion date'], ['organizationName', 'Organization name']]
  };

  const getTemplateVariables = (type) => TEMPLATE_VARIABLES[type] || [['companyName', 'Your company name'], ['customerName', 'Customer/org name']];

  // Only template types that a sender actually uses are shown — the others are enum scaffolding with
  // no email trigger yet, so configuring them would have no effect.
  const WIRED_TEMPLATE_TYPES = ['INVOICE_SEND', 'WORK_ORDER_CREATED', 'WORK_ORDER_COMPLETED'];
  const visibleTemplates = templates.filter(t => WIRED_TEMPLATE_TYPES.includes(t.templateType));

  const getTemplateTypeLabel = (type) => {
    const labels = {
      'INVOICE_SEND': 'Invoice Sent',
      'INVOICE_REMINDER': 'Invoice Reminder',
      'INVOICE_PAID': 'Invoice Paid',
      'QUOTATION_SEND': 'Quotation Sent',
      'QUOTATION_APPROVED': 'Quotation Approved',
      'QUOTATION_REJECTED': 'Quotation Rejected',
      'WORK_ORDER_CREATED': 'Work Order Created',
      'WORK_ORDER_COMPLETED': 'Work Order Completed'
    };
    return labels[type] || type;
  };

  if (loading && !selectedTypingCenter) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Shown as Settings › Communication › Email; the tab names the page. */}

      {!canEdit && <Alert severity="info" sx={{ mb: 2 }}>You have view-only access to these settings.</Alert>}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Typing Center Selector */}
      {isPlatform && typingCenters.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Select Typing Center</InputLabel>
            <Select
              value={selectedTypingCenter}
              label="Select Typing Center"
              onChange={(e) => setSelectedTypingCenter(e.target.value)}
            >
              {typingCenters.map(tc => (
                <MenuItem key={tc.id} value={tc.id}>
                  {tc.name} ({tc.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        sx={{ mb: 1.5, minHeight: 34, '& .MuiTab-root': { minHeight: 34, py: 0, textTransform: 'none', fontSize: '0.8125rem' }, '& .MuiTab-root .MuiSvgIcon-root': { fontSize: 16, mr: 0.75 } }}
      >
        <Tab icon={<SettingsIcon />} iconPosition="start" label="SMTP Settings" />
        <Tab icon={<TemplateIcon />} iconPosition="start" label="Email Templates" />
      </Tabs>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Tab 0: SMTP Settings */}
          {activeTab === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      SMTP Configuration
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={config.enabled}
                          onChange={handleConfigChange('enabled')}
                          disabled={!canEdit}
                          color="primary"
                        />
                      }
                      label={config.enabled ? 'Enabled' : 'Disabled'}
                    />
                  </Box>

                  {verified && (
                    <Alert severity="success" icon={<VerifiedIcon />} sx={{ mb: 3 }}>
                      Email verified on {verifiedAt ? new Date(verifiedAt).toLocaleDateString() : 'N/A'}
                    </Alert>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Email Provider</InputLabel>
                        <Select
                          value={selectedProvider}
                          label="Email Provider"
                          onChange={handleProviderChange}
                          disabled={!canEdit}
                        >
                          {emailProviders.map(p => (
                            <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="SMTP Host"
                        value={config.smtpHost}
                        onChange={handleConfigChange('smtpHost')}
                        fullWidth
                        disabled={!canEdit || selectedProvider !== 'CUSTOM'}
                        helperText={selectedProvider !== 'CUSTOM' ? 'Auto-configured for selected provider' : ''}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="SMTP Port"
                        value={config.smtpPort}
                        onChange={handleConfigChange('smtpPort')}
                        fullWidth
                        type="number"
                        disabled={!canEdit || selectedProvider !== 'CUSTOM'}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={config.smtpUseTls}
                            onChange={handleConfigChange('smtpUseTls')}
                            disabled={!canEdit}
                          />
                        }
                        label="Use TLS"
                        sx={{ mt: 1 }}
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" fontWeight={600} mb={1.25}>
                    Authentication
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="SMTP Username / Email"
                        value={config.smtpUsername}
                        onChange={handleConfigChange('smtpUsername')}
                        disabled={!canEdit}
                        fullWidth
                        helperText="Your email address for authentication"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="SMTP Password / App Password"
                        value={config.smtpPassword}
                        onChange={handleConfigChange('smtpPassword')}
                        disabled={!canEdit}
                        fullWidth
                        type="password"
                        placeholder="Leave empty to keep existing password"
                        helperText="Use App Password for Gmail/Outlook"
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" fontWeight={600} mb={1.25}>
                    Sender Information
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="From Name"
                        value={config.fromName}
                        onChange={handleConfigChange('fromName')}
                        disabled={!canEdit}
                        fullWidth
                        placeholder="Your Company Name"
                        helperText="Display name for sent emails"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="From Email"
                        value={config.fromEmail}
                        onChange={handleConfigChange('fromEmail')}
                        disabled={!canEdit}
                        fullWidth
                        placeholder={config.smtpUsername || 'noreply@company.com'}
                        helperText="Must match SMTP username for most providers"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Reply-To Email (Optional)"
                        value={config.replyToEmail}
                        onChange={handleConfigChange('replyToEmail')}
                        disabled={!canEdit}
                        fullWidth
                        placeholder="replies@company.com"
                        helperText="Email address for replies"
                      />
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" fontWeight={600} mb={1.25}>
                    Default CC Recipients
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {canEdit && (
                        <Box display="flex" gap={1} mb={1}>
                          <TextField
                            label="Add CC Email"
                            value={ccEmailInput}
                            onChange={(e) => setCcEmailInput(e.target.value)}
                            fullWidth
                            size="small"
                            onKeyPress={(e) => e.key === 'Enter' && handleAddCcEmail()}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleAddCcEmail}
                            sx={{ textTransform: 'none' }}
                          >
                            Add
                          </Button>
                        </Box>
                      )}
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {config.defaultCcEmails.map((email, index) => (
                          <Chip
                            key={index}
                            label={email}
                            onDelete={canEdit ? () => handleRemoveCcEmail(email) : undefined}
                            size="small"
                          />
                        ))}
                        {config.defaultCcEmails.length === 0 && (
                          <Typography variant="body2" color="text.secondary">
                            No default CC recipients
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>

                  {canEdit && (
                    <Box mt={2} display="flex" justifyContent="flex-end">
                      <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                        sx={{ minWidth: 150 }}
                      >
                        {saving ? 'Saving...' : 'Save Settings'}
                      </Button>
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Test Email Panel */}
              <Grid item xs={12} md={4}>
                {canEdit && (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} mb={1.25}>
                      Test Email Configuration
                    </Typography>

                    <Alert severity="info" sx={{ mb: 2 }}>
                      Save your settings first, then send a test email to verify the configuration works.
                    </Alert>

                    <TextField
                      label="Test Email Address"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      fullWidth
                      type="email"
                      placeholder="your@email.com"
                      sx={{ mb: 2 }}
                    />

                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={testingEmail ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      onClick={handleTestEmail}
                      disabled={testingEmail || !config.smtpHost || !config.smtpUsername}
                      color="secondary"
                    >
                      {testingEmail ? 'Sending...' : 'Send Test Email'}
                    </Button>

                    {!config.smtpHost && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        Please configure and save SMTP settings first
                      </Alert>
                    )}
                  </Paper>
                )}

                {/* Help Panel */}
                <Paper variant="outlined" sx={{ p: 2, mt: canEdit ? 2 : 0 }}>
                  <Typography variant="subtitle1" fontWeight={600} mb={1.25}>
                    Setup Guide
                  </Typography>

                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Gmail:</strong> Use an App Password instead of your regular password.
                    Go to Google Account &gt; Security &gt; 2-Step Verification &gt; App Passwords.
                  </Typography>

                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Zoho:</strong> Use your Zoho email and password. Enable SMTP access in Zoho Mail settings.
                  </Typography>

                  <Typography variant="body2" color="text.secondary" paragraph>
                    <strong>Outlook/Office 365:</strong> Use an App Password if 2FA is enabled.
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    <strong>Custom SMTP:</strong> Contact your email provider for SMTP server details.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Tab 1: Email Templates — one compact card: list, then placeholders */}
          {activeTab === 1 && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>Email templates</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1.5 }}>
                The emails sent to customers. Use placeholders like {'{customerName}'} or {'{companyName}'}.
              </Typography>

              {visibleTemplates.length === 0 ? (
                <Alert severity="info">
                  No email templates configured. Templates will be created automatically when you save SMTP settings.
                </Alert>
              ) : (
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {visibleTemplates.map((template, index) => {
                    const active = template.isActive !== false;
                    return (
                      <Box key={template.id || index}
                        onClick={canEdit ? () => handleEditTemplate(template) : undefined}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 2, px: 1.5, py: 0.75,
                          borderTop: index ? 1 : 0, borderColor: 'divider',
                          cursor: canEdit ? 'pointer' : 'default', '&:hover': canEdit ? { bgcolor: 'action.hover' } : {},
                        }}>
                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, width: 200, flexShrink: 0 }}>
                          {getTemplateTypeLabel(template.templateType)}
                        </Typography>
                        <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', flex: 1, minWidth: 0 }} noWrap>
                          {template.subject || 'Subject not set'}
                        </Typography>
                        <Chip size="small" label={active ? 'Active' : 'Inactive'} color={active ? 'success' : 'default'}
                          sx={{ height: 20, fontSize: '0.6875rem' }} />
                        {canEdit && (
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleEditTemplate(template); }} aria-label="Edit template">
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}

              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mt: 2, mb: 0.75 }}>
                Available placeholders
              </Typography>
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' } }}>
                {[
                  ['General', [['companyName', 'Your company name'], ['customerName', 'Customer / org name'], ['currency', 'Currency (AED)']]],
                  ['Invoice', [['invoiceNumber', 'Invoice number'], ['grandTotal', 'Total amount'], ['dueDate', 'Payment due date']]],
                  ['Quotation', [['quotationNumber', 'Quotation number'], ['validUntil', 'Validity date']]],
                  ['Work order', [['workOrderNumber', 'Work order number'], ['expectedCompletion', 'Expected date']]],
                ].map(([group, vars]) => (
                  <Box key={group}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 0.25 }}>{group}</Typography>
                    {vars.map(([name, desc]) => (
                      <Typography key={name} sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.6 }}>
                        <code>{`{${name}}`}</code> {desc}
                      </Typography>
                    ))}
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </>
      )}

      {/* Template Edit Dialog */}
      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600, py: 1.5 }}>
          Edit template · {selectedTemplate && getTemplateTypeLabel(selectedTemplate.templateType)}
        </DialogTitle>
        <DialogContent dividers sx={{
          '& .MuiInputBase-root, & .MuiInputLabel-root': { fontSize: '0.8125rem' },
          '& .MuiFormHelperText-root': { fontSize: '0.6875rem' },
        }}>
          {selectedTemplate && (
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TextField
                  size="small"
                  label="Subject"
                  value={selectedTemplate.subject || ''}
                  onChange={(e) => setSelectedTemplate(prev => ({ ...prev, subject: e.target.value }))}
                  onFocus={() => { lastFieldRef.current = 'subject'; }}
                  inputRef={subjectRef}
                  fullWidth
                />
                <FormControlLabel
                  sx={{ flexShrink: 0, mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' } }}
                  control={
                    <Switch
                      size="small"
                      checked={Boolean(selectedTemplate.isHtml ?? selectedTemplate.is_html)}
                      onChange={(e) => setSelectedTemplate(prev => ({ ...prev, isHtml: e.target.checked }))}
                    />
                  }
                  label="HTML"
                />
              </Box>
              <TextField
                size="small"
                label="Body"
                value={selectedTemplate.bodyTemplate || ''}
                onChange={(e) => setSelectedTemplate(prev => ({ ...prev, bodyTemplate: e.target.value }))}
                onFocus={() => { lastFieldRef.current = 'bodyTemplate'; }}
                inputRef={bodyRef}
                fullWidth
                multiline
                minRows={(selectedTemplate.isHtml ?? selectedTemplate.is_html) ? 14 : 9}
                maxRows={22}
                InputProps={{ sx: (selectedTemplate.isHtml ?? selectedTemplate.is_html) ? { fontFamily: 'monospace', fontSize: '0.75rem' } : undefined }}
                helperText={(selectedTemplate.isHtml ?? selectedTemplate.is_html) ? 'HTML markup.' : 'Plain text.'}
              />

              {/* Placeholders for THIS template type — click to insert at the cursor */}
              <Box>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 0.75 }}>
                  Insert placeholder <Typography component="span" sx={{ fontSize: '0.6875rem', fontWeight: 400 }}>— click to add it where the cursor is</Typography>
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {getTemplateVariables(selectedTemplate.templateType).map(([name, desc]) => (
                    <Tooltip key={name} title={desc}>
                      <Chip
                        size="small"
                        label={`{${name}}`}
                        onClick={() => insertPlaceholder(name)}
                        sx={{ height: 22, fontSize: '0.6875rem', fontFamily: 'monospace' }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setTemplateDialogOpen(false)} sx={{ textTransform: 'none', fontSize: '0.8125rem' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            disabled={savingTemplate}
            startIcon={savingTemplate ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
            sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
          >
            {savingTemplate ? 'Saving…' : 'Save template'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailConfig;
